import { useMemo } from 'react';
import { TRAINING } from './content';
import type { Track } from './content/types';
import type { TrainingViewer } from './lib/access';
import { guidedScreens, guidedTopicIds } from './lib/guided';

/**
 * The first screen: which role are you?
 *
 * Everything below the fold used to be the problem. The guide opens with twelve
 * shared topics before a PM reaches anything about their own job, and a track
 * picker rendered as a row of pills — visually a filter, not a decision, and
 * easy to scroll straight past. Asking the question outright, once, turns the
 * same choice into the thing the page is for.
 *
 * ⚠️ Picking a role does NOT change anyone's access. It chooses which track to
 * walk; the content inside is still filtered by what the reader can actually
 * reach, so choosing "Administrator" out of curiosity teaches nobody a button
 * they don't have. That's also why every track stays openable (bar the admin
 * one, which carries its own rule).
 */

const css = `
  .sh-wrap { max-width: 860px; margin: 0 auto; padding: var(--ew-s-6) var(--ew-s-6) var(--ew-s-8); }
  .sh-eyebrow { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: .09em;
                text-transform: uppercase; color: var(--ew-green); margin-bottom: 10px; }
  .sh-h { font: var(--ew-fw-bold) 27px/1.2 var(--ew-font); color: var(--ew-dark); margin: 0 0 8px; }
  .sh-lede { font: var(--ew-fw-regular) 15px/1.65 var(--ew-font); color: var(--ew-n-600); margin: 0 0 var(--ew-s-6); }
  .sh-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 12px; }
  .sh-card { text-align: left; background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
             padding: 15px 16px; cursor: pointer; font: inherit; display: flex; flex-direction: column; gap: 6px;
             transition: border-color .12s ease, box-shadow .12s ease; }
  .sh-card:hover { border-color: var(--ew-green); box-shadow: 0 1px 6px rgba(0,0,0,.06); }
  .sh-card.mine { border-color: var(--ew-green); box-shadow: 0 0 0 3px rgba(75,138,86,.10); }
  .sh-card-t { font: var(--ew-fw-semibold) 15px/1.3 var(--ew-font); color: var(--ew-dark);
               display: flex; align-items: center; gap: 8px; }
  .sh-you { font: var(--ew-fw-semibold) 9.5px/1.6 var(--ew-font); letter-spacing: .06em; text-transform: uppercase;
            background: var(--ew-green); color: #fff; border-radius: var(--ew-r-pill); padding: 1px 8px; }
  .sh-card-d { font: var(--ew-fw-regular) 12.5px/1.55 var(--ew-font); color: var(--ew-n-600); margin: 0; }
  .sh-card-n { font: var(--ew-fw-medium) 11.5px/1 var(--ew-font); color: var(--ew-n-500); margin-top: auto; padding-top: 4px; }
  .sh-foot { margin-top: var(--ew-s-6); border-top: 1px solid var(--ew-n-100); padding-top: var(--ew-s-4);
             font: var(--ew-fw-regular) 13px/1.6 var(--ew-font); color: var(--ew-n-500); }
  .sh-foot button { border: none; background: none; padding: 0; cursor: pointer; font: var(--ew-fw-semibold) 13px/1.6 var(--ew-font);
                    color: var(--ew-quality); border-bottom: 1px solid currentColor; }
`;

export default function StartHere({
  viewer, tracks, suggested, onPick, onBrowse,
}: {
  viewer: TrainingViewer;
  tracks: Track[];
  suggested?: Track;
  onPick: (trackId: string) => void;
  onBrowse: () => void;
}) {
  // The honest number: how many screens THIS reader's walkthrough actually has,
  // after access filtering. A fixed count per track would be wrong for anyone
  // whose access trims a section.
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of tracks) m.set(t.id, guidedTopicIds(guidedScreens(viewer, t.id)).length);
    return m;
  }, [tracks, viewer]);

  return (
    <div className="sh-wrap">
      <style>{css}</style>

      <div className="sh-eyebrow">Start here</div>
      <h1 className="sh-h">{TRAINING.title}</h1>
      <p className="sh-lede">
        Pick the job you do and we’ll walk you through it one step at a time — the handful of things
        everyone needs first, then the part that’s yours. You can stop anywhere; it remembers where
        you got to. Nothing here changes what you can do in the tracker.
      </p>

      <div className="sh-grid">
        {tracks.map((t) => {
          const n = counts.get(t.id) ?? 0;
          return (
            <button
              key={t.id}
              type="button"
              className={`sh-card${suggested?.id === t.id ? ' mine' : ''}`}
              onClick={() => onPick(t.id)}
            >
              <span className="sh-card-t">
                {t.label}
                {suggested?.id === t.id && <span className="sh-you">your role</span>}
              </span>
              <p className="sh-card-d">{t.audience}</p>
              <span className="sh-card-n">{n} {n === 1 ? 'topic' : 'topics'}</span>
            </button>
          );
        })}
      </div>

      <div className="sh-foot">
        Already know what you’re after? <button type="button" onClick={onBrowse}>Open the full guide</button>{' '}
        — every topic on one searchable page, with the screen-by-screen reference and a Word copy.
      </div>
    </div>
  );
}
